// src/pages/Compliance/CompliancePage.jsx
// Dedicated Compliance Records page — full table + analytics
// Route: /compliance

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Table, Tag, Button, Spin, Input, Select,
  Tooltip, message as antMsg,
} from 'antd';
import {
  ReloadOutlined, SearchOutlined, DownloadOutlined,
  CheckCircleOutlined, CloseCircleOutlined,
  SafetyCertificateOutlined,
} from '@ant-design/icons';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip as RTooltip, Legend, ResponsiveContainer, Cell,
} from 'recharts';
import dashboardService from '../../services/dashboardService';
import dayjs from 'dayjs';
import * as XLSX from 'xlsx';
import './CompliancePage.css';

// ── Design tokens ────────────────────────────────────────────────
const C = {
  navy:'#1B2D6B', blue:'#1E6FBF', lblue:'#EAF1F8',
  green:'#16a34a', lgreen:'#F0FDF4',
  red:'#DC2626',   lred:'#FEF2F2',
  amber:'#B45309', lamber:'#FFFBEB',
  teal:'#0891b2',  lteal:'#ecfeff',
  purple:'#7c3aed',lpurple:'#f5f3ff',
  text1:'#1a2d4a', text2:'#4a6580', text3:'#7a96b0',
  border:'#DCE6F0', surf:'#F5F8FC', white:'#FFFFFF',
};

const PIE_COLORS = ['#16a34a','#1E6FBF','#0891b2','#7c3aed','#b45309','#dc2626','#0e7490'];

// ── helpers ──────────────────────────────────────────────────────
const isYes = v => (v||'').trim().toLowerCase() === 'yes';

const PctBar = ({pct=0, color=C.blue, h=8}) => (
  <div style={{height:h, background:'#eaf1f8', borderRadius:20, overflow:'hidden', flex:1}}>
    <div style={{height:'100%', width:`${Math.min(pct,100)}%`,
      background:color, borderRadius:20, transition:'width .5s ease'}}/>
  </div>
);

const CompTag = ({value, trueColor=C.green, trueBg=C.lgreen, falseBg=C.lred, falseColor=C.red}) => {
  const yes = isYes(value);
  return (
    <Tag style={{borderRadius:20, fontWeight:700, fontSize:11,
      background: yes ? trueBg : falseBg,
      color:       yes ? trueColor : falseColor,
      border:`1px solid ${yes ? '#86efac' : '#fca5a5'}`,
      padding:'2px 10px',
    }}>
      {yes ? '✅ Yes' : '❌ No'}
    </Tag>
  );
};

// ── ChartTip ─────────────────────────────────────────────────────
const ChartTip = ({active, payload, label}) => {
  if (!active || !payload?.length) return null;
  return (
    <div style={{background:C.white, border:`1px solid ${C.border}`,
      borderRadius:10, padding:'10px 14px',
      boxShadow:'0 4px 12px rgba(0,0,0,0.1)'}}>
      <div style={{fontWeight:700, fontSize:12, color:C.text1, marginBottom:6}}>{label}</div>
      {payload.map(p => (
        <div key={p.name} style={{fontSize:12, color:p.fill||p.color,
          fontWeight:600, display:'flex', gap:8, marginBottom:2}}>
          <span>{p.name}:</span>
          <span style={{fontWeight:800}}>{p.value}</span>
        </div>
      ))}
    </div>
  );
};

// ══════════════════════════════════════════════════════════════════
export default function CompliancePage() {
  const navigate  = useNavigate();
  const [data,     setData]     = useState([]);
  const [loading,  setLoading]  = useState(false);
  const [search,   setSearch]   = useState('');
  const [filterTGP, setFilterTGP]   = useState('all');
  const [filterAck, setFilterAck]   = useState('all');
  const [filterInd, setFilterInd]   = useState('all');
  const [page,     setPage]     = useState(1);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await dashboardService.getCompliance();
      const rows = Array.isArray(res.data) ? res.data
        : Array.isArray(res.data?.data) ? res.data.data : [];
      setData(rows);
    } catch {
      antMsg.error('Failed to load compliance data');
      setData([]);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  // ── Analytics ────────────────────────────────────────────────
  const stats = useMemo(() => {
    if (!data.length) return null;
    const total   = data.length;
    const tgpDone = data.filter(r => isYes(r.tgpCompliance)).length;
    const ackDone = data.filter(r => isYes(r.acknowledgementCompliance)).length;
    const indDone = data.filter(r => isYes(r.inductionCompliance)).length;
    const allDone = data.filter(r =>
      isYes(r.tgpCompliance) && isYes(r.acknowledgementCompliance) && isYes(r.inductionCompliance)
    ).length;
    const noneDone = data.filter(r =>
      !isYes(r.tgpCompliance) && !isYes(r.acknowledgementCompliance) && !isYes(r.inductionCompliance)
    ).length;
    const partial  = total - allDone - noneDone;

    // Designation breakdown
    const desgMap = {};
    data.forEach(r => {
      const d = r.designation || 'Unknown';
      if (!desgMap[d]) desgMap[d] = { designation:d, tgp:0, ack:0, ind:0, total:0 };
      desgMap[d].total++;
      if (isYes(r.tgpCompliance))             desgMap[d].tgp++;
      if (isYes(r.acknowledgementCompliance)) desgMap[d].ack++;
      if (isYes(r.inductionCompliance))       desgMap[d].ind++;
    });
    const byDesignation = Object.values(desgMap)
      .sort((a,b) => b.total - a.total).slice(0, 12);

    return { total, tgpDone, ackDone, indDone, allDone, noneDone, partial, byDesignation };
  }, [data]);

  // ── Filtered rows ────────────────────────────────────────────
  const filtered = useMemo(() => {
    return data.filter(r => {
      if (search) {
        const s = search.toLowerCase();
        if (!(r.empCode||'').toLowerCase().includes(s) &&
            !(r.designation||'').toLowerCase().includes(s)) return false;
      }
      if (filterTGP !== 'all' && (isYes(r.tgpCompliance) ? 'yes' : 'no') !== filterTGP) return false;
      if (filterAck !== 'all' && (isYes(r.acknowledgementCompliance) ? 'yes' : 'no') !== filterAck) return false;
      if (filterInd !== 'all' && (isYes(r.inductionCompliance) ? 'yes' : 'no') !== filterInd) return false;
      return true;
    });
  }, [data, search, filterTGP, filterAck, filterInd]);

  // ── Export ───────────────────────────────────────────────────
  function exportExcel() {
    const rows = filtered.map(r => ({
      'Emp Code':                   r.empCode || '',
      'Designation':                r.designation || '',
      'TGP Compliance':             r.tgpCompliance || '',
      'Acknowledgement (KT)':       r.acknowledgementCompliance || '',
      'Induction (Joint Visit)':    r.inductionCompliance || '',
      'Status':                     isYes(r.tgpCompliance) && isYes(r.acknowledgementCompliance) && isYes(r.inductionCompliance)
                                      ? 'Full' : (!isYes(r.tgpCompliance) && !isYes(r.acknowledgementCompliance) && !isYes(r.inductionCompliance))
                                        ? 'None' : 'Partial',
      'Created Date':               r.createdDate ? dayjs(r.createdDate).format('DD MMM YYYY') : '',
      'Modified Date':              r.modifiedDate ? dayjs(r.modifiedDate).format('DD MMM YYYY') : '',
    }));
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Compliance');
    XLSX.writeFile(wb, `Compliance_${dayjs().format('YYYY-MM-DD')}.xlsx`);
    antMsg.success('Exported!');
  }

  const kpiChips = stats ? [
    { icon:'📋', label:'Total Records',
      value:stats.total, color:C.navy, bg:'#eff6ff' },
    { icon:'✅', label:'TGP Compliant',
      value:stats.tgpDone,
      sub:`${Math.round((stats.tgpDone/stats.total)*100)}%`,
      color:C.green, bg:C.lgreen },
    { icon:'📝', label:'Acknowledgement (KT)',
      value:stats.ackDone,
      sub:`${Math.round((stats.ackDone/stats.total)*100)}%`,
      color:C.blue, bg:C.lblue },
    { icon:'🎓', label:'Induction (Joint Visit)',
      value:stats.indDone,
      sub:`${Math.round((stats.indDone/stats.total)*100)}%`,
      color:C.teal, bg:C.lteal },
    { icon:'🏆', label:'Fully Compliant',
      value:stats.allDone,
      sub:`${Math.round((stats.allDone/stats.total)*100)}%`,
      color:C.green, bg:C.lgreen },
    { icon:'🔄', label:'Partial',
      value:stats.partial,
      sub:`${Math.round((stats.partial/stats.total)*100)}%`,
      color:C.amber, bg:C.lamber },
    { icon:'⚠️', label:'Non-Compliant',
      value:stats.noneDone,
      sub:`${Math.round((stats.noneDone/stats.total)*100)}%`,
      color:C.red, bg:C.lred },
  ] : [];

  const columns = [
    { title:'#', width:50, render:(_,__,i)=>
        <span style={{color:C.text3, fontSize:11}}>{(page-1)*15+i+1}</span> },
    { title:'Emp Code', dataIndex:'empCode', width:120,
      render:v=><span style={{fontFamily:'monospace',fontWeight:700,color:C.navy,fontSize:13}}>{v||'—'}</span> },
    { title:'Designation', dataIndex:'designation', width:160, ellipsis:true,
      render:v=><span style={{fontSize:12,color:C.text2,fontWeight:600}}>{v||'—'}</span> },
    { title:'TGP Compliance', dataIndex:'tgpCompliance', width:140, align:'center',
      render:v=><CompTag value={v}/> },
    { title:'Acknowledgement (KT)', dataIndex:'acknowledgementCompliance', width:170, align:'center',
      render:v=><CompTag value={v} trueColor={C.blue} trueBg={C.lblue}/> },
    { title:'Induction (Joint Visit)', dataIndex:'inductionCompliance', width:170, align:'center',
      render:v=><CompTag value={v} trueColor={C.teal} trueBg={C.lteal}/> },
    { title:'Status', width:120, align:'center',
      render:(_,r)=>{
        const t = isYes(r.tgpCompliance);
        const a = isYes(r.acknowledgementCompliance);
        const ind = isYes(r.inductionCompliance);
        const all = t&&a&&ind, none = !t&&!a&&!ind;
        return (
          <Tag style={{borderRadius:20, fontWeight:700, fontSize:11, padding:'3px 12px',
            background:all?C.lgreen:none?C.lred:C.lamber,
            color:all?C.green:none?C.red:C.amber,
            border:`1px solid ${all?'#86efac':none?'#fca5a5':'#fde68a'}`}}>
            {all?'🏆 Full':none?'⚠️ None':'🔄 Partial'}
          </Tag>
        );
      }},
    { title:'Created', dataIndex:'createdDate', width:110, align:'center',
      render:v=>v?<span style={{fontSize:11,color:C.text3}}>{dayjs(v).format('DD MMM YY')}</span>:'—' },
    { title:'Updated', dataIndex:'modifiedDate', width:110, align:'center',
      render:v=>v?<span style={{fontSize:11,color:C.text3}}>{dayjs(v).format('DD MMM YY')}</span>:'—' },
  ];

  return (
    <div className="comp-root">

      {/* ── Page header ─────────────────────────────────────────── */}
      <div style={{
        background:`linear-gradient(135deg,${C.navy} 0%,#2a4a9f 100%)`,
        padding:'22px 28px', marginBottom:24,
        boxShadow:'0 4px 20px rgba(27,45,107,0.25)',
      }}>
        <div style={{display:'flex',justifyContent:'space-between',
          alignItems:'center',flexWrap:'wrap',gap:12}}>
          <div>
            <div style={{fontSize:22,fontWeight:800,color:'#fff',
              letterSpacing:'-0.5px',display:'flex',alignItems:'center',gap:10}}>
              🛡️ Compliance Records
            </div>
            <div style={{fontSize:12,color:'rgba(255,255,255,0.55)',marginTop:3}}>
              TGP · Acknowledgement (Knowledge Transfer) · Induction (Joint Visit)
              &nbsp;·&nbsp;{dayjs().format('DD MMMM YYYY')}
            </div>
          </div>
          <div style={{display:'flex',gap:8,flexWrap:'wrap'}}>
            <Button icon={<ReloadOutlined/>} onClick={load} loading={loading}
              style={{borderRadius:8,background:'rgba(255,255,255,0.12)',
                border:'1px solid rgba(255,255,255,0.25)',color:'#fff',fontWeight:600}}>
              Refresh
            </Button>
            <Button icon={<DownloadOutlined/>} onClick={exportExcel}
              style={{borderRadius:8,background:'#fff',border:'none',
                color:C.navy,fontWeight:700}}>
              Export Excel
            </Button>
          </div>
        </div>
      </div>

      <div style={{padding:'0 24px 48px'}}>

        {/* ── KPI Chips ─────────────────────────────────────────── */}
        {loading && !data.length
          ? <div style={{textAlign:'center',padding:48}}><Spin size="large"/></div>
          : (
            <>
              <div style={{display:'grid',
                gridTemplateColumns:'repeat(7,1fr)',
                gap:12,marginBottom:24}}>
                {kpiChips.map((c,i)=>(
                  <div key={i} className="comp-chip"
                    style={{background:c.bg,borderTopColor:c.color}}>
                    <div style={{fontSize:22,marginBottom:6}}>{c.icon}</div>
                    <div style={{fontSize:24,fontWeight:800,color:c.color,lineHeight:1}}>
                      {c.value??'—'}
                    </div>
                         <div style={{fontSize:10,fontWeight:700,color:C.text3,
                      textTransform:'uppercase',letterSpacing:'0.5px',marginTop:4,
                      lineHeight:1.3}}>
                      {c.label}
                    </div>
                    {c.sub&&<div style={{fontSize:12,fontWeight:700,color:c.color,marginTop:2}}>
                      {c.sub}
                    </div>}
                  </div>
                ))}
              </div>

              {/* ── Progress bars + Bar chart ──────────────────── */}
              <div style={{display:'grid',gridTemplateColumns:'380px 1fr',
                gap:16,marginBottom:24}}>

                {/* Progress bars */}
                <div style={{background:C.white,borderRadius:14,
                  border:`1px solid ${C.border}`,padding:'20px 22px',
                  boxShadow:'0 1px 4px rgba(0,0,0,0.04)'}}>
                  <div style={{fontWeight:700,fontSize:14,color:C.text1,marginBottom:4}}>
                    Overall Compliance Rate
                  </div>
                  <div style={{fontSize:11,color:C.text3,marginBottom:18}}>
                    {stats?.total||0} total employees
                  </div>
                  {stats && [
                    {label:'TGP Compliance',                  done:stats.tgpDone, color:C.green},
                    {label:'Knowledge Transfer',done:stats.ackDone, color:C.blue},
                    {label:'Joint Visit',          done:stats.indDone, color:C.teal},
                    // {label:'Fully Compliant (all 3)',          done:stats.allDone, color:C.purple},
                  ].map(row=>{
                    const pct = stats.total ? Math.round((row.done/stats.total)*100) : 0;
                    return (
                      <div key={row.label} style={{marginBottom:14}}>
                        <div style={{display:'flex',justifyContent:'space-between',
                          fontSize:12,fontWeight:600,color:C.text2,marginBottom:5}}>
                          <span>{row.label}</span>
                          <span style={{fontWeight:800,color:row.color}}>
                            {pct}%
                            <span style={{color:C.text3,fontWeight:400,marginLeft:4}}>
                              ({row.done}/{stats.total})
                            </span>
                          </span>
                        </div>
                        <div style={{display:'flex',alignItems:'center',gap:10}}>
                          <PctBar pct={pct} color={row.color} h={10}/>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Bar chart by designation */}
                <div style={{background:C.white,borderRadius:14,
                  border:`1px solid ${C.border}`,padding:'20px 22px',
                  boxShadow:'0 1px 4px rgba(0,0,0,0.04)'}}>
                  <div style={{fontWeight:700,fontSize:14,color:C.text1,marginBottom:4}}>
                    Compliance by Designation
                  </div>
                  <div style={{fontSize:11,color:C.text3,marginBottom:16}}>
                    Top 12 designations — TGP vs knowledge Transfer vs Induction
                  </div>
                  {stats?.byDesignation?.length ? (
                    <ResponsiveContainer width="100%" height={240}>
                      <BarChart data={stats.byDesignation}
                        margin={{top:4,right:8,left:-10,bottom:65}}>
                        <CartesianGrid strokeDasharray="3 3" stroke={C.border} vertical={false}/>
                        <XAxis dataKey="designation" interval={0}
                          axisLine={false} tickLine={false} height={65}
                          tick={({x,y,payload})=>(
                            <g transform={`translate(${x},${y})`}>
                              <text x={0} y={0} dy={10} textAnchor="end"
                                fill={C.text2} fontSize={10} fontWeight={600}
                                transform="rotate(-35)">
                                {payload.value.length>16
                                  ? payload.value.slice(0,16)+'…' : payload.value}
                              </text>
                            </g>
                          )}/>
                        <YAxis allowDecimals={false}
                          tick={{fontSize:11,fill:C.text3}}
                          axisLine={false} tickLine={false}/>
                        <RTooltip content={<ChartTip/>}/>
                        <Legend wrapperStyle={{fontSize:11,paddingTop:6}}/>
                        <Bar dataKey="tgp" name="TGP"             fill={C.green}  radius={[3,3,0,0]} maxBarSize={16}/>
                        <Bar dataKey="ack" name="knowledge Transfer" fill={C.blue}   radius={[3,3,0,0]} maxBarSize={16}/>
                        <Bar dataKey="ind" name="Joint Call"       fill={C.teal}   radius={[3,3,0,0]} maxBarSize={16}/>
                      </BarChart>
                    </ResponsiveContainer>
                  ) : (
                    <div style={{textAlign:'center',padding:48,color:C.text3}}>No data</div>
                  )}
                </div>
              </div>

              {/* ── Filters + Table ────────────────────────────── */}
              <div style={{background:C.white,borderRadius:16,
                border:`1px solid ${C.border}`,overflow:'hidden',
                boxShadow:'0 1px 4px rgba(0,0,0,0.04)'}}>

                {/* Table header */}
                <div style={{
                  padding:'14px 20px',
                  background:`linear-gradient(135deg,${C.navy},#2a4a9f)`,
                  display:'flex',justifyContent:'space-between',
                  alignItems:'center',flexWrap:'wrap',gap:12,
                }}>
                  <div style={{fontWeight:700,fontSize:14,color:'#fff',
                    display:'flex',alignItems:'center',gap:8}}>
                    <SafetyCertificateOutlined/>
                    Compliance Records
                    <span style={{fontSize:12,fontWeight:400,
                      opacity:.7,marginLeft:4}}>
                      {filtered.length} / {data.length} records
                    </span>
                  </div>
                  <div style={{display:'flex',gap:8,flexWrap:'wrap',alignItems:'center'}}>
                    <Input
                      prefix={<SearchOutlined style={{color:'rgba(255,255,255,.5)'}}/>}
                      placeholder="Search code / designation…"
                      value={search}
                      onChange={e=>{setSearch(e.target.value);setPage(1);}}
                      style={{width:200,borderRadius:8,
                        background:'rgba(255,255,255,0.12)',
                        border:'1px solid rgba(255,255,255,0.25)',
                        color:'#fff'}}
                      allowClear
                    />
                    {[
                      {label:'TGP', val:filterTGP, set:setFilterTGP},
                      {label:'Ack', val:filterAck, set:setFilterAck},
                      {label:'Ind', val:filterInd, set:setFilterInd},
                    ].map(f=>(
                      <Select key={f.label} size="small" value={f.val}
                        onChange={v=>{f.set(v);setPage(1);}}
                        style={{width:100}}
                        options={[
                          {value:'all',label:`${f.label}: All`},
                          {value:'yes',label:`${f.label}: ✅ Yes`},
                          {value:'no', label:`${f.label}: ❌ No`},
                        ]}/>
                    ))}
                  </div>
                </div>

                <Table
                  size="small"
                  dataSource={filtered}
                  columns={columns}
                  rowKey={(r,i)=>r.id??i}
                  loading={loading}
                  scroll={{x:1050}}
                  pagination={{
                    current:page, pageSize:15,
                    onChange:p=>setPage(p),
                    showTotal:t=>`${t} records`,
                    showSizeChanger:false,
                    style:{padding:'12px 16px',
                      background:C.surf,
                      borderTop:`1px solid ${C.border}`},
                  }}
                  rowClassName={(_,i)=>i%2===0?'':'comp-alt-row'}
                />
              </div>
            </>
          )
        }
      </div>
    </div>
  );
}